import { Component, Input, OnDestroy, OnInit } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { map } from "rxjs/operators";
import { select, Store } from "@ngrx/store";
import { AppState, loadedProductsTableConfig } from "src/app/store/reducers";
import { getProductsExcelList, getProductsList, resetProductsState } from "src/app/store/actions/products/product.actions";

import {
  ColumnSorterInfo,
  DefaultPage,
  ExportEvent,
  FilterEvent,
  FilterName,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import {
  removeColumnSorterInfoOptions,
  removeTableConfigOptions,
  setAdvancedFilter,
  setColumnInfoCategoriesProperties,
} from "src/app/shared/utils/tableUtils";

import { BaseProductsList } from "src/app/shared/base-classes/base-products-list/base-products-list";

const USAGE_CODE = "PRODUCTGROUP.PRODUCT";

@Component({
  selector: "product-group-products-list",
  templateUrl: "./product-group-products-list.component.html",
  styleUrls: ["./product-group-products-list.component.scss"],
})
export class ProductGroupProductsComponent
  extends BaseProductsList
  implements OnInit, OnDestroy
{
  @Input() set code(code: string) {
    if (code) {
      this._code = code;
      this.getProductCategoriesProperties(USAGE_CODE, this.getProducts.bind(this));
    }
  }

  _code: string;

  constructor(store: Store<AppState>, translate: TranslateService) {
    super(store, translate);

    this.columnInfo$ = this.store.pipe(select(loadedProductsTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          columnInfo = removeColumnSorterInfoOptions(columnInfo);
          columnInfo = setColumnInfoCategoriesProperties(
            columnInfo,
            this.tableConfig
          );
          return columnInfo;
        }
      })
    );
  }

  ngOnInit(): void {
    this.setTranslatedTitle();
    this.tableConfig = removeTableConfigOptions(this.tableConfig);
  }

  getProducts(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.store.dispatch(
      getProductsList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: advancedFilter,
        },
      })
    );
  }

  getProductsExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    const advancedFilter = this.setAdvancedFilter(filterEvent);

    this.store.dispatch(
      getProductsExcelList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: advancedFilter,
        },
      })
    );
  }

  setAdvancedFilter(filterEvent: FilterEvent) {
    const productGroupProductsFilter = {
      column: "group.code",
      operator: FilterName.EQUALS_TO,
      value: this._code,
    };

    return setAdvancedFilter(productGroupProductsFilter, filterEvent);
  }

  onGetTableData(tableEvent: TableEvent) {
    this.getTableData(tableEvent, this.getProducts.bind(this));
  }

  onGetExportData(exportEvent: ExportEvent) {
    this.getExportData(exportEvent, this.getProductsExcelList.bind(this));
  }

  onSaveTableConfig(columnInfo: ColumnSorterInfo[]) {
    this.saveTableConfig(columnInfo, USAGE_CODE);
  }

  ngOnDestroy() {
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    this.store.dispatch(resetProductsState());
  }
}
